import { useCallback, useEffect, useRef, useState } from "react";
import { MapContainer, Marker, TileLayer, Tooltip, useMap } from "react-leaflet";
import { Cloud, Loader2, LocateFixed } from "lucide-react";
import L from "leaflet";
import { getAccessToken, getApiRequestUrl } from "@/api/treeplet";
import { getMildewPresentation } from "@/components/MildewStatus";

const mildewColors = {
  NO_RISK: "#4E795B",
  RISK: "#A94442",
  UNKNOWN: "#9E7000",
};

const displayDeviceId = (deviceId) => deviceId.slice(-8);
const OPENWEATHER_ATTRIBUTION = '<a href="https://openweathermap.org/" target="_blank" rel="noreferrer">Weather data provided by OpenWeather</a>';

const mildewIcon = (mildew) => {
  const state = mildew?.state === "NO_RISK" || mildew?.state === "RISK" ? mildew.state : "UNKNOWN";
  const color = mildewColors[state];
  const stale = mildew?.evaluation_status === "STALE"
    || (mildew?.inputs_current === false && mildew?.evaluation_status !== "NEVER_EVALUATED");
  const glyph = state === "NO_RISK" ? "✓" : state === "RISK" ? "!" : "?";
  return L.divIcon({
    className: "",
    html: `<div style="position:relative;display:flex;align-items:center;justify-content:center;">
      <div style="width:22px;height:22px;border-radius:50%;background:${color};border:3px ${stale ? "dashed" : "solid"} #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);opacity:${stale ? ".62" : "1"};color:#fff;font:700 13px/16px sans-serif;text-align:center;">${glyph}</div>
      ${stale ? `<span aria-hidden="true" style="position:absolute;right:-5px;bottom:-5px;width:12px;height:12px;border-radius:50%;background:#6B7280;color:#fff;border:1px solid #fff;font:700 8px/10px sans-serif;text-align:center;">◷</span>` : ""}
    </div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
};

class AuthenticatedCloudGridLayer extends L.GridLayer {
  createTile(coords, done) {
    const tile = document.createElement("img");
    tile.alt = "";
    tile.setAttribute("role", "presentation");

    const controller = new AbortController();
    tile.__treepletAbortController = controller;
    const token = getAccessToken();
    if (!token) {
      done(new Error("Authentication required"), tile);
      return tile;
    }

    fetch(getApiRequestUrl(`/weather/cloud-tiles/${coords.z}/${coords.x}/${coords.y}.png`), {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then((response) => {
        if (response.status === 401) window.dispatchEvent(new Event("treeplet:unauthorized"));
        if (!response.ok) {
          const error = new Error("Cloud tile request failed");
          error.status = response.status;
          throw error;
        }
        return response.blob();
      })
      .then((blob) => {
        if (controller.signal.aborted) return;
        const objectUrl = URL.createObjectURL(blob);
        tile.__treepletObjectUrl = objectUrl;
        tile.onload = () => done(null, tile);
        tile.onerror = () => done(new Error("Cloud tile image failed"), tile);
        tile.src = objectUrl;
      })
      .catch((error) => {
        if (!controller.signal.aborted) done(error, tile);
      });

    return tile;
  }

  _removeTile(key) {
    const tile = this._tiles[key]?.el;
    tile?.__treepletAbortController?.abort();
    if (tile?.__treepletObjectUrl) URL.revokeObjectURL(tile.__treepletObjectUrl);
    super._removeTile(key);
  }
}

function CloudOverlay({ enabled, onStateChange }) {
  const map = useMap();

  useEffect(() => {
    if (!enabled) {
      onStateChange("off");
      return undefined;
    }

    onStateChange("loading");
    const layer = new AuthenticatedCloudGridLayer({
      opacity: 0.8,
      maxNativeZoom: 18,
      maxZoom: 20,
      zIndex: 350,
      tileSize: 256,
    });
    const handleLoad = () => onStateChange("ready");
    const handleTileError = (event) => onStateChange(event.error?.status === 401 ? "auth-error" : "error");

    layer.on("load", handleLoad);
    layer.on("tileerror", handleTileError);
    map.attributionControl?.addAttribution(OPENWEATHER_ATTRIBUTION);
    layer.addTo(map);

    return () => {
      layer.off("load", handleLoad);
      layer.off("tileerror", handleTileError);
      map.removeLayer(layer);
      map.attributionControl?.removeAttribution(OPENWEATHER_ATTRIBUTION);
    };
  }, [enabled, map, onStateChange]);

  return null;
}

function Viewport({ positions, fallbackCenter, fallbackZoom, recenterRequest }) {
  const map = useMap();
  const hasSetInitialViewport = useRef(false);

  const centerMap = useCallback((animate) => {
    if (positions.length > 1) {
      map.fitBounds(positions, { padding: [40, 40], maxZoom: 17, animate });
    } else if (positions.length === 1) {
      if (animate) {
        map.flyTo(positions[0], 17, { duration: 0.5 });
      } else {
        map.setView(positions[0], 17);
      }
    } else if (animate) {
      map.flyTo(fallbackCenter, fallbackZoom, { duration: 0.5 });
    } else {
      map.setView(fallbackCenter, fallbackZoom);
    }
  }, [fallbackCenter, fallbackZoom, map, positions]);

  useEffect(() => {
    map.attributionControl?.setPrefix(false);
    if (!hasSetInitialViewport.current) {
      centerMap(false);
      hasSetInitialViewport.current = true;
    }
  }, [centerMap, map]);

  useEffect(() => {
    if (recenterRequest > 0) centerMap(true);
  }, [centerMap, recenterRequest]);

  return null;
}

export const VineyardLeafletMap = ({
  devices = [],
  viewportPositions = [],
  fallbackCenter = [41.362, 1.71],
  fallbackZoom = 13,
  onDeviceClick,
  height = "100%",
}) => (
  <MapWithCloudOverlay
    devices={devices}
    viewportPositions={viewportPositions}
    fallbackCenter={fallbackCenter}
    fallbackZoom={fallbackZoom}
    onDeviceClick={onDeviceClick}
    height={height}
  />
);

const MapWithCloudOverlay = ({
  devices = [],
  viewportPositions = [],
  fallbackCenter = [41.362, 1.71],
  fallbackZoom = 13,
  onDeviceClick,
  height = "100%",
}) => {
  const [cloudEnabled, setCloudEnabled] = useState(false);
  const [cloudState, setCloudState] = useState("off");
  const [recenterRequest, setRecenterRequest] = useState(0);
  const onCloudStateChange = useCallback((nextState) => setCloudState(nextState), []);

  return (
    <div style={{ height, width: "100%" }} className="rounded-lg overflow-hidden border border-line">
    <style>{`@keyframes tp-ping{0%{transform:scale(1);opacity:.5}100%{transform:scale(2.6);opacity:0}}`}</style>
    <div className="relative h-full">
    <MapContainer center={fallbackCenter} zoom={fallbackZoom} maxZoom={20} style={{ height: "100%", width: "100%" }} scrollWheelZoom zoomControl>
      <TileLayer
        attribution='&copy; <a href="https://www.esri.com/">Esri</a> &middot; Vantor &middot; Earthstar Geographics &middot; GIS User Community'
        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        maxZoom={20}
        maxNativeZoom={20}
      />
      <CloudOverlay enabled={cloudEnabled} onStateChange={onCloudStateChange} />
      <Viewport positions={viewportPositions} fallbackCenter={fallbackCenter} fallbackZoom={fallbackZoom} recenterRequest={recenterRequest} />
      {devices.map((device) => {
        const mildew = getMildewPresentation(device.mildew);
        return <Marker
          key={device.id}
          position={device.position}
          icon={mildewIcon(device.mildew)}
          eventHandlers={{ click: () => onDeviceClick?.(device) }}
        >
          <Tooltip direction="top" offset={[0, -10]}>
            <div className="text-xs leading-5"><div className="font-semibold">{displayDeviceId(device.id)}</div><div>Mildiu: {mildew.label}</div><div className="text-ink-secondary">{mildew.evaluationLabel}</div></div>
          </Tooltip>
        </Marker>;
      })}
    </MapContainer>
    <div className="absolute top-4 right-4 z-[1000] flex flex-col items-end gap-2">
      <button
        type="button"
        data-testid="map-recenter"
        aria-label="Centrar mapa"
        title="Centrar mapa"
        onClick={() => setRecenterRequest((request) => request + 1)}
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-line bg-surface/95 text-ink shadow-sm backdrop-blur hover:bg-surface"
      >
        <LocateFixed size={17} aria-hidden="true" />
      </button>
      <button
        type="button"
        data-testid="cloud-toggle"
        aria-pressed={cloudEnabled}
        onClick={() => setCloudEnabled((enabled) => !enabled)}
        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold shadow-sm backdrop-blur ${cloudEnabled ? "border-brand bg-brand text-white" : "border-line bg-surface/95 text-ink"}`}
      >
        {cloudState === "loading" ? <Loader2 className="animate-spin" size={15} /> : <Cloud size={15} />}
        Nubes
      </button>
      {cloudEnabled && ["error", "auth-error"].includes(cloudState) && (
        <div role="alert" className="max-w-[220px] rounded-md border border-line bg-surface/95 px-3 py-2 text-right text-xs text-critical shadow-sm">
          No se pudo cargar la cobertura nubosa.
        </div>
      )}
    </div>
    </div>
  </div>
  );
};

export default VineyardLeafletMap;
